/**
 * 受講者プロフィール集約ルート
 *
 * 1ブラウザ = 1受講者の概念で設計された不変オブジェクト。
 * リポジトリを通じて永続化する。
 */
export class LearnerProfile {
  private constructor(
    private wantsAdvancedCourse: boolean,
    private readonly nickname: string | null,
    private readonly createdAt: Date,
    private updatedAt: Date
  ) {}

  /**
   * 新しい受講者プロフィールを作成
   * @param wantsAdvancedCourse - アドバンスト編受講希望（デフォルト: false）
   */
  static create(wantsAdvancedCourse = false): LearnerProfile {
    const now = new Date();
    return new LearnerProfile(wantsAdvancedCourse, null, now, now);
  }

  /**
   * 永続化データから受講者プロフィールを復元
   */
  static reconstruct(data: {
    wantsAdvancedCourse: boolean;
    nickname: string | null;
    createdAt: Date;
    updatedAt: Date;
  }): LearnerProfile {
    return new LearnerProfile(
      data.wantsAdvancedCourse,
      data.nickname,
      data.createdAt,
      data.updatedAt
    );
  }

  /**
   * アドバンスト編受講希望を設定
   * @param wants - true: 受講したい、false: 受講しない
   * @returns 新しいLearnerProfileインスタンス（不変オブジェクトパターン）
   */
  setWantsAdvancedCourse(wants: boolean): LearnerProfile {
    return new LearnerProfile(
      wants,
      this.nickname,
      this.createdAt,
      new Date() // updatedAtを更新
    );
  }

  /**
   * ニックネームを設定（1回のみ設定可能）
   * @param nickname - 設定するニックネーム
   * @returns 新しいLearnerProfileインスタンス
   * @throws Error - 既にニックネームが設定されている場合
   */
  setNickname(nickname: string): LearnerProfile {
    if (this.nickname !== null) {
      throw new Error(
        'ニックネームは既に設定されています。変更することはできません。'
      );
    }

    if (!nickname || nickname.trim().length === 0) {
      throw new Error('ニックネームを入力してください。');
    }

    return new LearnerProfile(
      this.wantsAdvancedCourse,
      nickname.trim(),
      this.createdAt,
      new Date() // updatedAtを更新
    );
  }

  // ゲッター
  getWantsAdvancedCourse(): boolean {
    return this.wantsAdvancedCourse;
  }

  getNickname(): string | null {
    return this.nickname;
  }

  getCreatedAt(): Date {
    return this.createdAt;
  }

  getUpdatedAt(): Date {
    return this.updatedAt;
  }

  /**
   * 永続化用のデータに変換
   */
  toData(): {
    wantsAdvancedCourse: boolean;
    nickname: string | null;
    createdAt: string;
    updatedAt: string;
  } {
    return {
      wantsAdvancedCourse: this.wantsAdvancedCourse,
      nickname: this.nickname,
      createdAt: this.createdAt.toISOString(),
      updatedAt: this.updatedAt.toISOString(),
    };
  }
}
